import { useState, useRef, useEffect } from "react";
import { useBusinesses } from "@/hooks/use-businesses";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

interface Message {
  id: number;
  content: string;
  role: "user" | "assistant";
  createdAt: string;
}

export function AIChat({ onClearHistory }: { onClearHistory?: () => void }) {
  const { activeBusiness } = useBusinesses();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState("");
  const [isNewSession, setIsNewSession] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const businessId = activeBusiness?.id;

  // Fetch chat messages - start with empty array for new sessions
  const {
    data: messages = [],
    isLoading,
    isError,
  } = useQuery<Message[]>({
    queryKey: [`/api/businesses/${businessId}/chat`],
    enabled: !!businessId && !isNewSession,
    initialData: [],
  });

  // Send message mutation
  const sendMessageMutation = useMutation({
    mutationFn: async (content: string) => {
      if (!businessId) throw new Error("No active business selected");
      const response = await apiRequest("POST", `/api/businesses/${businessId}/chat`, { content });
      return response.json();
    },
    onSuccess: (newMessages) => {
      // If this is a new session, enable message fetching and clear any old data
      if (isNewSession) {
        setIsNewSession(false);
        queryClient.setQueryData<Message[]>([`/api/businesses/${businessId}/chat`], newMessages);
      } else {
        queryClient.setQueryData<Message[]>([`/api/businesses/${businessId}/chat`], (old = []) => [
          ...old,
          ...newMessages,
        ]);
      }
      setMessage("");
    },
    onError: (err: any) => {
      toast({
        title: "Failed to send message",
        description: err.message || "An error occurred while sending your message",
        variant: "destructive",
      });
    },
  });

  // Clear chat history function
  const clearChatHistory = async () => {
    if (!businessId) return;
    
    try {
      // Call the delete endpoint
      await apiRequest("DELETE", `/api/businesses/${businessId}/chat`);
      
      // Reset to new session state
      setIsNewSession(true);
      queryClient.setQueryData<Message[]>([`/api/businesses/${businessId}/chat`], []);
      
      toast({
        title: "Chat history cleared",
        description: "Ready for a new conversation",
      });
      
      if (onClearHistory) onClearHistory();
    } catch (error) {
      console.error("Error clearing chat history:", error);
      toast({
        title: "Failed to clear chat history",
        description: "An error occurred while clearing messages",
        variant: "destructive",
      });
    }
  };

  // Expose clear function to parent component
  useEffect(() => {
    if (onClearHistory) {
      // Store the clear function so parent can call it
      (window as any).clearChatHistory = clearChatHistory;
    }
    return () => {
      delete (window as any).clearChatHistory;
    };
  }, [businessId, onClearHistory]);

  // Auto scroll to the latest message only when messages change and there are messages
  useEffect(() => {
    // Only scroll if there are messages and we're not in the initial loading state
    if (messagesEndRef.current && (messages as Message[]).length > 0 && !isLoading) {
      // Use a container check to prevent scrolling the whole page
      const container = messagesEndRef.current.closest('.overflow-y-auto');
      if (container) {
        container.scrollTop = container.scrollHeight;
      } else {
        // Only scroll the element into view within its container
        messagesEndRef.current.scrollIntoView({ block: 'end', behavior: 'smooth' });
      }
    }
  }, [messages, isLoading]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;
    
    sendMessageMutation.mutate(message);
  };

  // Render loading state
  if (isLoading) {
    return (
      <div className="flex flex-col h-full items-center justify-center p-6">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="mt-4 text-neutral-500">Loading chat history...</p>
      </div>
    );
  }

  // Render error state
  if (isError) {
    return (
      <div className="flex flex-col h-full items-center justify-center p-6">
        <p className="text-destructive">Failed to load chat messages. Please try again.</p>
        <Button 
          variant="outline" 
          className="mt-4"
          onClick={() => queryClient.invalidateQueries({ queryKey: [`/api/businesses/${businessId}/chat`] })}
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {(messages as Message[]).length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="bg-primary/10 p-4 rounded-full">
              <Send className="h-8 w-8 text-primary" />
            </div>
            <h3 className="mt-4 text-lg font-medium">Welcome to ZentroBooks AI Assistant</h3>
            <p className="mt-2 text-neutral-500 max-w-md">
              Ask me questions about your finances, transactions, or bookkeeping tasks.
              I'm here to help you understand your business finances better.
            </p>
          </div>
        ) : (
          (messages as Message[]).map((msg: Message) => (
            <div
              key={msg.id}
              className={`chat-message ${
                msg.role === "user" ? "message-user" : "message-ai"
              } ${msg.role === "user" ? "ml-auto" : ""}`}
            >
              <p className="text-sm">{msg.content}</p>
              <div className="text-xs text-neutral-500 mt-1">
                {new Date(msg.createdAt).toLocaleTimeString()}
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
        
        {sendMessageMutation.isPending && (
          <div className="chat-message message-ai">
            <div className="flex items-center space-x-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <p className="text-sm">Thinking...</p>
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-neutral-200 p-4">
        <form onSubmit={handleSubmit} className="flex items-end gap-2">
          <Textarea
            className="min-h-24 flex-1"
            placeholder="Ask a question about your finances..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={sendMessageMutation.isPending}
          />
          <Button
            type="submit"
            size="icon"
            className="h-10 w-10"
            disabled={!message.trim() || sendMessageMutation.isPending}
          >
            {sendMessageMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </form>
        <div className="mt-2 text-xs text-neutral-500">
          Try asking: "What were my top expenses last month?" or "How much revenue did I generate in Q2?"
        </div>
      </div>
    </div>
  );
}
